import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  Checkbox,
  Container,
  Button,
  Tooltip,
  TooltipProps,
  tooltipClasses,
  CircularProgress,
  TablePagination,
  TextField,
} from '@mui/material';
import Paper from '@mui/material/Paper';
import { useStyles } from '../../styles';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Stack, styled } from '@mui/system';
import ToggleTable from './toggleTable';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import RemoveCircleOutlineOutlinedIcon from '@mui/icons-material/RemoveCircleOutlineOutlined';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';

import {
  fetchRequestFormDataCreator,
  fetchRequestFormInnerDataCreator,
  fetchReviewFormApprovalDataCreator,
  fetchReviewFormProductDataCreator,
  fetchFormApprovalReviewCreator,
} from '../../../redux-api/slice';
import { updateRequestPayload } from '../../../redux/tabName';
import { strings } from '@app/common/strings';
import { ToastType } from '@app/services/toaster';
import { ToasterService, NetworkService } from '@app/services';
import dateUtils from '@app/screens/zho/utils/dateFormat';
import theme from '@app/themes';
import { API_PATH, BYPASS_ZHO_REQUEST_ELIGIBLE_STATUSES } from '@app/common/constants';

interface CheckBoxState {
  id: number;
  checked: boolean;
}

type SortKey =
  | 'requestFormNo'
  | 'createdOn'
  | 'candFId'
  | 'customer'
  | 'hq'
  | 'status'
  | 'chemist'
  | 'mrName';

export const RequestFormTable = ({
  handleToggleModal,
  setRequestFormId,
  userLoginInfo,
  modalOpen,
  setModalOpen,
}: any) => {
  const classes = useStyles();
  const { DateFormat } = dateUtils;
  const [openCompetitor, setOpenCompetitor] = useState({ id: 0, open: false });
  const [requestIds, setRequestIds] = useState<number[]>([]);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [isMounted, setIsMounted] = useState(false);
  const requestID = useRef('');
  const [jumpPage, setJumpPage] = useState('');
  const dispatch = useDispatch();
  const tableData = useSelector(
    (state: RootStateOrAny) => state.fcDashBoard.requestformData,
  );

  const statusToggleNo = useSelector(
    (state: RootStateOrAny) => state.tabNames.value.statusToggleNo,
  );

  const requestFormPayload = useSelector(
    (state: RootStateOrAny) => state.tabNames.value.requestPayload,
  );

  const RecallDetails = useSelector(
    (state: RootStateOrAny) =>
      state.fcDashBoard.reviewFormApprovalPeriodCompleted,
  );

  const ApprovalReviewCompleted = useSelector(
    (state: RootStateOrAny) => state.fcDashBoard.formApprovalReviewCompleted,
  );

  const requestFormDataLoader = useSelector(
    (state: RootStateOrAny) => state.fcDashBoard.requestFormLoading,
  );

  const [checkBox, setCheckBox] = useState<CheckBoxState[]>([]);
  const [bypassingRows, setBypassingRows] = useState<Set<number>>(new Set());
  const [sortConfig, setSortConfig] = useState<
    { key: SortKey; direction: 'asc' | 'desc' } | null
  >(null);

  const handleSort = (key: SortKey) => {
    setSortConfig(prev => {
      if (prev && prev.key === key) {
        return {
          key,
          direction: prev.direction === 'asc' ? 'desc' : 'asc',
        };
      }
      return { key, direction: 'asc' };
    });
  };

  const getComparableValue = (row: any, key: SortKey) => {
    if (key === 'customer') {
      const customerName = row?.stockistNamelist?.[0];
      return customerName ? customerName.toLowerCase() : '';
    }
    const value = row?.[key];
    if (value === null || value === undefined) {
      if (key === 'createdOn') {
        return Number.MIN_SAFE_INTEGER;
      }
      return '';
    }
    if (key === 'createdOn') {
      const timeValue = new Date(value).getTime();
      return Number.isNaN(timeValue) ? Number.MIN_SAFE_INTEGER : timeValue;
    }
    if (typeof value === 'string') {
      return value.toLowerCase();
    }
    return value;
  };

  const sortedRequestList = useMemo(() => {
    const list = tableData?.requestList
      ? tableData.requestList.map((item: any, index: number) => ({
        ...item,
        originalIndex: index,
      }))
      : [];

    if (!sortConfig) {
      return list;
    }

    const { key, direction } = sortConfig;
    return [...list].sort((a, b) => {
      const valueA = getComparableValue(a, key);
      const valueB = getComparableValue(b, key);
      if (valueA < valueB) {
        return direction === 'asc' ? -1 : 1;
      }
      if (valueA > valueB) {
        return direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [tableData?.requestList, sortConfig]);

  const renderSortableHeader = (
    label: string,
    columnKey: SortKey,
    options?: { sx?: any; className?: string },
  ) => {
    const isActive = sortConfig?.key === columnKey;
    const icon = isActive
      ? sortConfig?.direction === 'asc'
        ? <ArrowUpwardIcon sx={{ fontSize: 16 }} />
        : <ArrowDownwardIcon sx={{ fontSize: 16 }} />
      : <UnfoldMoreIcon sx={{ fontSize: 16, color: 'action.active' }} />;

    return (
      <TableCell
        onClick={() => handleSort(columnKey)}
        sx={{
          border: 'none',
          cursor: 'pointer',
          userSelect: 'none',
          ...options?.sx,
        }}
        className={options?.className}>
        <Stack direction="row" alignItems="center" gap={0.5}>
          <Typography
            component="span"
            variant="inherit"
            sx={{ fontSize: 'inherit', fontWeight: 'inherit', color: 'inherit' }}>
            {label}
          </Typography>
          <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
            {icon}
          </Box>
        </Stack>
      </TableCell>
    );
  };

  const toggleCheckBox = (type: any, id: number) => {
    setCheckBox((prevCheckBox: any) => {
      let updatedRequestIds: number[] = [...requestIds];

      if (type === 'all') {
        const allChecked = prevCheckBox.every(
          (item: any) => item.checked === true,
        );
        const newCheckBoxState = prevCheckBox.map((item: any) => ({
          ...item,
          checked: !allChecked,
        }));

        // Update requestIds based on allChecked status
        updatedRequestIds = allChecked
          ? []
          : tableData.requestList?.map(
            (request: { requestId: number }) => request.requestId,
          ) || [];

        setRequestIds(updatedRequestIds);
        return newCheckBoxState;
      } else {
        const newCheckBoxState = prevCheckBox.map((item: any) => {
          if (item.id === type) {
            return {
              ...item,
              checked: !item.checked,
            };
          }
          return item;
        });

        // Update requestIds based on individual checkbox state
        const isChecked = newCheckBoxState.find(
          (item: any) => item.id === type,
        )?.checked;
        if (isChecked) {
          updatedRequestIds.push(id);
        } else {
          updatedRequestIds = updatedRequestIds.filter(
            requestId => requestId !== id,
          );
        }

        setRequestIds(updatedRequestIds);
        return newCheckBoxState;
      }
    });
  };

  const getStatusColor = (status: number) => {
    if (status === 0) {
      return '#ff7650';
    } else if (status === 1) {
      return '#01a685';
    } else if (status === 3 || status === 4) {
      return '#f45b60';
    } else if (status === 5) {
      return '#34b53a';
    }
  };

  const handleApprovalReviewForm = () => {
    if (requestIds.length === 0) {
      ToasterService.hideToaster();
      ToasterService.showToaster(
        'Please select a request to Approve!',
        ToastType.ERROR,
      );
    }
    if (requestIds.length > 0) {
      dispatch(
        fetchFormApprovalReviewCreator(
          userLoginInfo?.staffPositionId,
          userLoginInfo?.userId,
          requestIds,
          'approval',
        ),
      );

      setRequestIds([]);

      // setTimeout(() => {
      //   setRequestIds([]);
      // }, 100);
    }
  };

  const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: theme.palette.common.white,
      color: 'rgba(0, 0, 0, 0.87)',
      // boxShadow: theme.shadows[1],
      fontSize: 11.7,
      border: '1px solid #dcdce0',
      borderRadius: '5px',
      padding: '0.3rem',
      boxShadow: 'rgba(149, 157, 165, 0.2) 0px 8px 24px',
    },
  }));

  useEffect(() => {
    dispatch(
      updateRequestPayload({
        ...requestFormPayload,
        currentStaffPositionId: userLoginInfo?.staffPositionId,
      }),
    );
    // dispatch(fetchStateDataCreator(userLoginInfo?.staffPositionId));
    dispatch(
      fetchRequestFormDataCreator(
        {
          ...requestFormPayload,
          pageSize: rowsPerPage,
          currentStaffPositionId: userLoginInfo?.staffPositionId,
        },
        1,
      ),
    );
    if (tableData?.requestList) {
      const initialCheckBoxState = tableData.requestList.map(
        (data: any, index: number) => ({
          id: index,
          checked: false,
        }),
      );
      setCheckBox(initialCheckBoxState);
    }
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (
      requestID.current !== undefined &&
      requestID.current !== null &&
      requestID.current !== ''
    ) {
      dispatch(fetchReviewFormProductDataCreator(requestID.current));
    }
  }, [RecallDetails]);

  useEffect(() => {
    if (isMounted) {
      dispatch(
        fetchRequestFormDataCreator(
          {
            ...requestFormPayload,
            currentStaffPositionId: userLoginInfo?.staffPositionId,
          },
          statusToggleNo,
        ),
      );
    }
    if (
      requestID.current !== undefined &&
      requestID.current !== null &&
      requestID.current !== ''
    ) {
      dispatch(fetchReviewFormApprovalDataCreator(requestID.current));
      dispatch(fetchReviewFormProductDataCreator(requestID.current));
    }

    if (tableData?.requestList) {
      const initialCheckBoxState = tableData.requestList.map(
        (data: any, index: number) => ({
          id: index,
          checked: false,
        }),
      );
      setCheckBox(initialCheckBoxState);
    }
  }, [ApprovalReviewCompleted]);

  useEffect(() => {
    if (tableData?.requestList) {
      const initialCheckBoxState = tableData.requestList.map(
        (data: any, index: number) => ({
          id: index,
          checked: false,
        }),
      );
      setCheckBox(initialCheckBoxState);
    }

    // setRequestIds([]);
  }, [tableData]);

  useEffect(() => {
    setPage(0);
    setRowsPerPage(10);
    setJumpPage('');
    setOpenCompetitor({ id: 0, open: false });
  }, [statusToggleNo]);

  const handleInnerClickData = (requestId: number, index: number) => {
    setOpenCompetitor({
      id: index,
      open: true,
    });
    dispatch(fetchRequestFormInnerDataCreator(requestId, false));
  };

  const handleTooltipOpen = (index: number) => {
    setModalOpen({ id: index, isOpen: true });
  };
  const handleTooltipClose = (index: number | null) => {
    setModalOpen({ id: index, isOpen: false });
  };

  const handleOpenReviewPopup = (requestId: number) => {
    handleToggleModal();
    handleTooltipClose(null);
    dispatch(fetchReviewFormApprovalDataCreator(requestId));
    dispatch(fetchReviewFormProductDataCreator(requestId));
  };

  const handleBypassRequest = async (requestId: number) => {
    setBypassingRows(prev => new Set(prev).add(requestId));
    try {
      const url = `${API_PATH.zho.fcDashboard.requestForm.bypassRequest}${requestId}`;
      const response: any = await NetworkService.post(url, {}, {});
      if (response?.data?.isError) {
        ToasterService.hideToaster();
        ToasterService.showToaster(
          response?.data?.errorMessage || 'Bypass failed. Please try again.',
          ToastType.ERROR,
        );
      } else {
        ToasterService.hideToaster();
        ToasterService.showToaster(
          response?.data?.response?.message || 'Request bypassed successfully!',
          ToastType.SUCCESS,
        );
        dispatch(
          fetchRequestFormDataCreator(
            {
              ...requestFormPayload,
              currentStaffPositionId: userLoginInfo?.staffPositionId,
            },
            statusToggleNo,
          ),
        );
      }
    } catch {
      ToasterService.hideToaster();
      ToasterService.showToaster('Something went wrong!', ToastType.ERROR);
    } finally {
      setBypassingRows(prev => {
        const next = new Set(prev);
        next.delete(requestId);
        return next;
      });
    }
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    handleTooltipClose(null);
    setPage(newPage);
    dispatch(
      fetchRequestFormDataCreator(
        {
          ...requestFormPayload,
          pageSize: rowsPerPage,
          pageNumber: newPage, // Send one-based page number to the API
          currentStaffPositionId: userLoginInfo?.staffPositionId,
        },
        statusToggleNo,
      ),
    );
    dispatch(
      updateRequestPayload({
        ...requestFormPayload,
        currentStaffPositionId: userLoginInfo?.staffPositionId,
        pageSize: rowsPerPage,
        pageNumber: newPage, // API expects one-based page number
      }),
    );
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const newPageSize = parseInt(event.target.value, 10);
    handleTooltipClose(null);
    setRowsPerPage(newPageSize);
    setPage(0); // Reset to the first page (zero-based for Material-UI)
    dispatch(
      fetchRequestFormDataCreator(
        {
          ...requestFormPayload,
          pageSize: newPageSize,
          pageNumber: 0, // API expects one-based page number
          currentStaffPositionId: userLoginInfo?.staffPositionId,
        },
        statusToggleNo,
      ),
    );
    dispatch(
      updateRequestPayload({
        ...requestFormPayload,
        currentStaffPositionId: userLoginInfo?.staffPositionId,
        pageSize: newPageSize,
        pageNumber: 0, // API expects one-based page number
      }),
    );
  };

  return (
    <Container
      sx={{
        marginTop: '10px',
        position: 'relative',
        maxWidth: '100% !important',
        padding: '0px  !important',
      }}>
      {statusToggleNo === 1 && tableData?.requestList?.length > 0 && (
        <Stack
          marginBottom="12px"
          direction="row"
          justifyContent="flex-end"
          alignItems="center">
          <Box>
            <Button
              data-testid="button-approve"
              variant="contained"
              className={classes.applyButton}
              onClick={handleApprovalReviewForm}>
              {strings.approve}
            </Button>
          </Box>
        </Stack>
      )}

      {!requestFormDataLoader ? (
        <>
          <TableContainer className={`${classes.noBoxShd}`} component={Paper}>
            <Table sx={{ borderCollapse: 'separate', borderSpacing: '0px 1px' }}>
              <TableHead>
                <TableRow className={`${classes.requestformTableHeading}   `}>
                  {statusToggleNo === 1 &&
                    tableData?.requestList?.length > 0 && (
                      <TableCell
                        sx={{ border: 'none', padding: '0px !important' }}>
                        <Checkbox
                          sx={{ color: '#322b7c !important' }}
                          data-testid="check-all-requestform"
                          checked={
                            checkBox?.length > 0 &&
                            checkBox.every(
                              (data: any) => data?.checked === true,
                            )
                          }
                          onClick={() => toggleCheckBox('all', 0)}
                        />
                      </TableCell>
                    )}
                  {renderSortableHeader(
                    strings.zho.fcAdmin.dashboard.tableHeading.requestFormNo,
                    'requestFormNo',
                    {
                      sx: {
                        paddingLeft: '44px',
                        minWidth: '180px !important',
                      },
                      className: classes.requestformTableHeading,
                    },
                  )}

                  {renderSortableHeader(strings.zho.creationDate, 'createdOn', {
                    sx: { padding: '0px', minWidth: '90px' },
                    className: classes.requestformTableHeading,
                  })}
                  {renderSortableHeader(strings.zho.candf, 'candFId', {
                    className: `${classes.requestformTableHeading} ${classes.cellPadding} ${classes.requestFormHeadingWidth}`,
                  })}
                  {renderSortableHeader(strings.zho.customer, 'customer', {
                    sx: { padding: '10px 15px', minWidth: '125px' },
                    className: classes.requestformTableHeading,
                  })}
                  {renderSortableHeader(strings.hq, 'hq', {
                    sx: { padding: '0px' },
                    className: `${classes.requestformTableHeading} ${classes.requestFormHeadingWidth}`,
                  })}
                  {renderSortableHeader(strings.status, 'status', {
                    sx: { padding: '10px' },
                    className: `${classes.requestformTableHeading} ${classes.requestFormHeadingWidth}`,
                  })}
                  {renderSortableHeader(
                    strings.zho.fcAdmin.dashboard.tableHeading.chemistandDis,
                    'chemist',
                    {
                      sx: { padding: '0px', textWrap: 'nowrap' },
                      className: classes.requestformTableHeading,
                    },
                  )}

                  {renderSortableHeader(strings.zho.mrName, 'mrName', {
                    sx: { padding: '0px 10px !important', minWidth: '100px' },
                    className: classes.requestformTableHeading,
                  })}
                  <TableCell
                    sx={{ border: 'none', padding: '10px !important' }}
                    className={`${classes.requestformTableHeading} `}>
                    {strings.actions}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedRequestList.length > 0 ? (
                  sortedRequestList.map((row: any) => {
                    const { originalIndex, ...data } = row;
                    const index = originalIndex;
                    return (
                      <Fragment key={data?.requestId || originalIndex}>
                        <TableRow className={classes.requestformTbleBoxSdw}>
                          {statusToggleNo === 1 && (
                            <TableCell
                              className={classes.checkBoxShd}
                              sx={{
                                border: 'none !important',
                                padding: '0px !important',
                              }}>
                              <Checkbox
                                sx={{ color: '#322b7c !important' }}
                                data-testid="check-requestform"
                                checked={checkBox[index]?.checked || false}
                                onClick={() =>
                                  toggleCheckBox(index, data?.requestId)
                                }
                              />
                            </TableCell>
                          )}
                          <TableCell
                            sx={{ padding: '0px 6px !important' }}
                            className={`  ${tableData?.requestList[index]?.isHighlighted
                              ? classes.highlightBorderwithStatus
                              : ''
                              } ${classes.requestFormTableFont} ${classes.productTableLeftBorder
                              } ${classes.borderTopLeft} `}>
                            <Stack
                              spacing={1}
                              direction="row"
                              alignItems="center">
                              <Box width="30px" display="inline-block">
                                <IconButton
                                  data-testid="show-comp-prod"
                                  onClick={e => {
                                    e.stopPropagation();
                                  }}>
                                  {/* <ReplayIcon color="primary" /> */}
                                  {openCompetitor.open &&
                                    openCompetitor.id === index ? (
                                    <RemoveCircleOutlineOutlinedIcon
                                      sx={{
                                        height: '18px',
                                        width: '18px',
                                      }}
                                      color="primary"
                                      onClick={() =>
                                        setOpenCompetitor({
                                          id: index,
                                          open: false,
                                        })
                                      }
                                    />
                                  ) : (
                                    <AddCircleOutlineOutlinedIcon
                                      sx={{ height: '18px', width: '18px' }}
                                      color="primary"
                                      onClick={() =>
                                        handleInnerClickData(
                                          data?.requestId,
                                          index,
                                        )
                                      }
                                    />
                                  )}
                                </IconButton>
                              </Box>
                              <Box
                                onClick={() => {
                                  handleOpenReviewPopup(data?.requestId);
                                  setRequestFormId(data?.requestId);
                                  requestID.current = data?.requestId;
                                }}
                                sx={{
                                  color: theme.colors.primary,
                                  cursor: 'pointer',
                                  fontWeight: '500',
                                  textDecoration: 'underline',
                                }}
                                fontSize="11.7px"
                                minWidth="160px"
                                display="inline-block">
                                {data?.requestFormNo}
                              </Box>
                            </Stack>
                          </TableCell>
                          <TableCell
                            sx={{ padding: '0px !important' }}
                            className={`  ${tableData?.requestList[index]?.isHighlighted
                              ? classes.highlightBorderwithStatus
                              : ''
                              } ${classes.requestFormTableFont}`}>
                            {data?.createdOn !== null
                              ? DateFormat(data?.createdOn)
                              : '---'}
                          </TableCell>
                          <TableCell
                            className={` ${classes.cellPadding}  ${tableData?.requestList[index]?.isHighlighted
                              ? classes.highlightBorderwithStatus
                              : ''
                              } ${classes.requestFormTableFont}`}>
                            {data?.candFId}
                          </TableCell>
                          <TableCell
                            sx={{ padding: '10px 15px !important' }}
                            className={`  ${tableData?.requestList[index]?.isHighlighted
                              ? classes.highlightBorderwithStatus
                              : ''
                              } ${classes.requestFormTableFont}`}>
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={0.5}
                              justifyContent="space-between">
                              <Box>
                                {data?.stockistNamelist[0]?.length > 12
                                  ? `${data.stockistNamelist[0].slice(
                                    0,
                                    12,
                                  )}...`
                                  : data.stockistNamelist[0]}
                              </Box>
                              <Stack
                                direction="row"
                                justifyContent="center"
                                alignItems="center">
                                <LightTooltip
                                  title={
                                    <Stack
                                      direction="row"
                                      flexWrap="wrap"
                                      gap={1}
                                      justifyContent="start"
                                      alignItems="center">
                                      {data?.stockistNamelist.map(
                                        (data: any, index: number) => (
                                          <Typography
                                            fontSize="11.7px"
                                            fontWeight="400"
                                            variant="caption"
                                            key={index}>
                                            {data}
                                          </Typography>
                                        ),
                                      )}
                                    </Stack>
                                  }>
                                  <InfoOutlinedIcon
                                    data-testid="show-customers"
                                    sx={{ height: '15px', width: '15px' }}
                                    color="primary"
                                  />
                                </LightTooltip>
                              </Stack>
                            </Stack>
                          </TableCell>
                          <TableCell
                            sx={{
                              padding: '0px !important',
                              wordBreak: 'break-all !important',
                            }}
                            className={`  ${tableData?.requestList[index]?.isHighlighted
                              ? classes.highlightBorderwithStatus
                              : ''
                              } ${classes.requestFormTableFont}`}>
                            {data?.hq}
                          </TableCell>
                          <TableCell
                            sx={{ padding: '10px !important' }}
                            className={`  ${tableData?.requestList[index]?.isHighlighted
                              ? classes.highlightBorderwithStatus
                              : ''
                              } ${classes.requestFormTableFont}`}>
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              alignItems="center"
                              gap={1}>
                              <Typography
                                fontWeight="400"
                                className={classes.requestFormTableFont}
                                sx={{ color: getStatusColor(data?.statusCode) }}>
                                {data?.status}
                              </Typography>
                              {(statusToggleNo === 4 ||
                                data?.statusCode === 3) && (
                                  <LightTooltip
                                    title={
                                      <>
                                        <Typography
                                          fontSize="11.7px"
                                          fontWeight="400"
                                          variant="caption"
                                          key={index}>
                                          {data?.pendingAt}
                                        </Typography>
                                      </>
                                    }>
                                    <InfoOutlinedIcon
                                      data-testid="show-customers"
                                      sx={{
                                        height: '15px',
                                        width: '15px',
                                        color: '#DA3131',
                                      }}
                                      color="primary"
                                    />
                                  </LightTooltip>
                                )}
                            </Stack>
                          </TableCell>
                          <TableCell
                            sx={{ padding: '0px !important' }}
                            className={`  ${tableData?.requestList[index]?.isHighlighted
                              ? classes.highlightBorderwithStatus
                              : ''
                              } ${classes.requestFormTableFont}`}>
                            {data?.chemist}
                          </TableCell>
                          <TableCell
                            sx={{ padding: '10px !important' }}
                            className={`  ${tableData?.requestList[index]?.isHighlighted
                              ? classes.highlightBorderwithStatus
                              : ''
                              } ${classes.requestFormTableFont}`}>
                            {data?.mrName}
                          </TableCell>
                          <TableCell
                            sx={{ padding: '10px !important' }}
                            className={`  ${tableData?.requestList[index]?.isHighlighted
                              ? classes.highlightBorderwithStatus
                              : ''
                              } ${classes.requestFormTableFont}  ${classes.borderTopRight
                              } ${classes.productTableRightBorder}`}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              {BYPASS_ZHO_REQUEST_ELIGIBLE_STATUSES.includes(data?.status) && (
                                <Typography
                                  onClick={() => {
                                    if (!bypassingRows.has(data?.requestId)) {
                                      handleBypassRequest(data?.requestId);
                                    }
                                  }}
                                  data-testid="bypass-request"
                                  sx={{
                                    fontSize: '11.7px',
                                    cursor: bypassingRows.has(data?.requestId)
                                      ? 'not-allowed'
                                      : 'pointer',
                                    opacity: bypassingRows.has(data?.requestId)
                                      ? 0.5
                                      : 1,
                                    color: '#34b53a',
                                  }}
                                  variant="subtitle2">
                                  {bypassingRows.has(data?.requestId)
                                    ? 'Bypassing...'
                                    : 'Bypass'}
                                </Typography>
                              )}
                              <Typography
                                onClick={() => {
                                  handleOpenReviewPopup(data?.requestId);
                                  setRequestFormId(data?.requestId);
                                  requestID.current = data?.requestId;
                                }}
                                data-testid="review-request"
                                sx={{ fontSize: '11.7px', cursor: 'pointer' }}
                                variant="subtitle2"
                                color="primary">
                                {strings.zho.review}
                              </Typography>
                            </Stack>
                          </TableCell>
                        </TableRow>
                        {openCompetitor.open && openCompetitor.id === index && (
                          <TableRow>
                            {statusToggleNo === 1 && (
                              <TableCell sx={{ border: 'none' }}></TableCell>
                            )}
                            <TableCell
                              sx={{
                                padding: '0px 0px 8px 0px',
                                border: 'none !important',
                              }}
                              colSpan={7}>
                              <ToggleTable />
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell sx={{ textAlign: 'center' }} colSpan={9}>
                      Currently there are no request!
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box display="flex" alignItems="center" justifyContent="flex-end">
            <TablePagination
              component="div"
              rowsPerPageOptions={[10, 20, 50, 100]}
              count={+tableData?.totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              showFirstButton={true}
              showLastButton={true}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Records per page"
              labelDisplayedRows={({ page, count }) => {
                const totalPages = Math.ceil(count / rowsPerPage);
                return `${page + 1} of ${totalPages}`;
              }}
            />
            <Box display="flex" alignItems="center" ml={2} borderRadius={2} p={1}>
              <Box
                display="flex"
                alignItems="center"
                ml={2}
                borderRadius={1}
                p={1}
                sx={{
                  width: 60,
                  height: 40,
                  mr: 1,
                  border: '1px solid',
                  borderColor: 'black',
                  overflow: 'hidden'
                }}
              >
                <TextField
                  size="small"
                  type="number"
                  value={jumpPage}
                  placeholder="Page No."
                  onChange={(e) => setJumpPage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      const targetPage = Number(jumpPage) - 1;
                      if (targetPage >= 0 && targetPage < Math.ceil(tableData?.totalCount / rowsPerPage)) {
                        handleChangePage(null, targetPage);
                      }
                    }
                  }}
                  InputProps={{
                    disableUnderline: true,
                    inputProps: {
                      min: 1,
                      max: Math.ceil(tableData?.totalCount / rowsPerPage),
                      style: {
                        textAlign: 'center',
                        padding: 0,
                        height: '100%',
                        fontSize: '10px'
                      }
                    }
                  }}
                  variant="standard"
                  sx={{
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'transparent',
                    borderRadius: 2,
                    '& .MuiInputBase-root': {
                      height: '100%',
                    }
                  }}
                />
              </Box>
              <Button
                disabled={Math.ceil(tableData?.totalCount / rowsPerPage) <= 1}
                onClick={() => {
                  const targetPage = Number(jumpPage) - 1;
                  if (targetPage >= 0 && targetPage < Math.ceil(tableData?.totalCount / rowsPerPage)) {
                    handleChangePage(null, targetPage);
                  }
                }}
                data-testid="goButton"
                variant="contained"
                component="span"
                className={classes.requestFormgo}
                sx={{
                  maxWidth: '10px',
                  fontSize: '12.7px',
                  textTransform: 'capitalize',
                }}
                size="medium">
                {strings.go}
              </Button>
            </Box>

          </Box>

        </>
      ) : (
        <TableContainer sx={{ height: '90px' }}>
          <Table>
            <TableBody>
              <TableRow>
                <Box
                  // className={classes.loader}
                  sx={[
                    {
                      display: 'flex',
                      justifyContent: 'center',
                      mt: 3,
                    },
                  ]}>
                  <CircularProgress />
                </Box>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
};

export default RequestFormTable;
